import { useFlags } from '@atlaskit/flag';
import { ExportDeliveriesDialogFormFields } from 'core/config/form-fields';
import { useDialog } from 'core/providers/DialogProvider';
import { useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import SharedForm from 'shared/components/form/form';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { IExportDeliveriesDialogFormOutput } from './export-deliveries-dialog-interface';
import DeliveriesApiService from 'core/api/deliveries/deliveries-api.service';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
dayjs.extend(customParseFormat);

const ExportDeliveriesDialog = () => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const flags = useFlags();
  const dialog = useDialog();

  const exportCsv = async (startDateString: string, endDateString: string) => {
    const csv: string = await DeliveriesApiService.exportDeliveries(startDateString, endDateString);
    const blob = new Blob(['\ufeff', csv]);
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `pride_pharmacy_deliveries_${startDateString}_to_${endDateString}.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const exportSuccessfulDeliveriesPdf = async (startDateString: string, endDateString: string) => {
    const signedUrl: string = await DeliveriesApiService.exportSuccessfulDeliveries(startDateString, endDateString);
    window.open(signedUrl);
  };

  const submit = async (data: IExportDeliveriesDialogFormOutput) => {
    setError(undefined);
    setFormSubmitting(true);
    const { startDateString, endDateString, exportType } = data;
    if (startDateString > endDateString) {
      setError('Start date must be set after end date');
      setFormSubmitting(false);
      return;
    }

    try {
      if (exportType.value === 'csv') {
        await exportCsv(startDateString, endDateString);
      } else {
        await exportSuccessfulDeliveriesPdf(startDateString, endDateString);
      }
      showSuccessFlag('Export successful', 'The deliveries were successfully exported', flags);
      dialog?.closeDialog();
    } catch (error) {
      setFormSubmitting(false);
      showErrorFlag('Export failed', 'The export failed, please try again.', flags);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const customContent = () => {
    return (
      <SharedForm
        className='p-4 overflow-y-auto'
        onSubmit={submit}
        fields={ExportDeliveriesDialogFormFields}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
        formErrorMessage={error}
      />
    );
  };

  return <SharedDialogBase title='Export deliveries' customContentTemplate={customContent()} showButtons={false} />;
};

export default ExportDeliveriesDialog;
